"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { Suspense, useEffect, useRef } from "react";
import { scrubPreviewToken } from "@/lib/analytics/scrub-url";
import { logEvent } from "@/lib/lecturehub/analytics-actions";

function Tracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const last = useRef<string | null>(null);

  useEffect(() => {
    if (!pathname) return;
    const qs = searchParams.toString();
    // A CMS preview token lives in the URL and must not reach an analytics
    // event (lib/analytics/scrub-url.ts) — the view is still counted.
    const path = scrubPreviewToken(qs ? `${pathname}?${qs}` : pathname);
    if (last.current === path) return; // the effect can re-run; emit once per path
    last.current = path;
    void logEvent("page_view", { path });
  }, [pathname, searchParams]);

  return null;
}

/**
 * First-party `page_view` emitter (B17) — fires `logEvent` on client navigation
 * (App Router does no full reload). Mounted inside `AppShell`, which only ever
 * renders for **signed-in** users, so it never fires for guests (`logEvent` is
 * auth-only). Distinct from `PostHogPageView` (consent-gated, third-party); this
 * is the first-party company-brain signal (what employees actually view).
 *
 * `useSearchParams` must sit under `<Suspense>` or it opts the tree into CSR.
 */
export function PageView() {
  return (
    <Suspense fallback={null}>
      <Tracker />
    </Suspense>
  );
}
